import React, { useEffect } from 'react';
import { useFormik, FormikProvider } from 'formik';
import { useMutation, useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import * as yup from 'yup';
import propTypes from 'prop-types';
import runtime from '../../utils/runtime';
import firebase, { updateCurrentUser } from '../../services/firebase';
import CondominiumRegister from '../../components/CondominiumRegister';
import UserRegister from '../../components/UserRegister';
import useToast from '../../hooks/useToast';
import useMe from '../../hooks/useMe';
import useContext from '../../hooks/useContext';
import useQueryString from '../../hooks/useQueryString';
import { isValidCNPJ, isValidZipcode } from '../../utils/validators';
import { makePhoneNumber, showPhoneNumber } from '../../utils/masks';

const condominiumGql = gql`
  query condominiumBySlug($slug: String) {
    condominium(slug: $slug) {
      _id
    }
  }
`;

const registerGql = gql`
  mutation register($user: RegisterUser!, $condominium: RegisterCondominium) {
    register(user: $user, condominium: $condominium) {
      _id
      name
      email
      condominiums {
        data
        role
      }
    }
  }
`;

const userSchema = yup.object({
  name: yup.string().required(),
  country: yup.string().required(),
  phoneNumber: yup
    .string()
    .test({
      message: 'invalid-phone',
      // eslint-disable-next-line no-useless-escape
      test: a => a && a.match(/(\(\d{2}\)\s)(\d{4,5}\-\d{4})/gm),
    })
    .required(),
  email: yup
    .string()
    .email()
    .required(),
  unity: yup.string().when('slug', slug => (slug ? yup.string().required() : yup.string())),
  slug: yup.string(),
});

const condominiumSchema = yup.object({
  name: yup.string().required(),
  belongsTo: yup
    .string()
    .required()
    .default(runtime.RAZZLE_PROJECT || 'kiper'),
  doc: yup
    .string()
    .test('doc-validation', 'CNPJ inválido', doc => isValidCNPJ(doc))
    .required(),
  phoneNumber: yup
    .string()
    .test({
      message: 'invalid-phone',
      // eslint-disable-next-line no-useless-escape
      test: a => a && a.match(/(\(\d{2}\)\s)(\d{4,5}\-\d{4})/gm),
    })
    .required(),
  country: yup.string().required(),
  totalUnits: yup
    .number()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER)
    .required(),
  address: yup.object({
    zipcode: yup
      .string()
      .test('zipcode-validation', 'CEP inválido', zipcode => isValidZipcode(zipcode))
      .required(),
    number: yup
      .number()
      .min(0)
      .max(Number.MAX_SAFE_INTEGER)
      .required(),
  }),
});

export default function Register({ history }) {
  const toast = useToast();
  const [me] = useMe({ fetchPolicy: 'network-only' });
  const { write } = useContext();
  const [registerFn] = useMutation(registerGql, {
    onCompleted: async ({ register }) => {
      write({
        condominiumId: register.condominiums[0].data,
        callback: () => {
          toast.fire({
            icon: 'success',
            title: `Bem vindo(a) ${register.name}`,
          });
          history.push('/dashboard');
        },
      });
    },
    onError: () =>
      toast.fire({
        icon: 'error',
        title: 'Problemas ao registrar. Tente novamente',
      }),
  });
  const querystring = useQueryString();

  const formikBag = useFormik({
    initialValues: {
      step: 'user',
      user: {
        slug: querystring.get('slug') || '',
        name: '',
        country: '+55',
        phoneNumber: '',
        email: '',
        unity: '',
      },
      condominium: {
        name: '',
        doc: '',
        country: '+55',
        phoneNumber: '',
        totalUnits: '',
        address: {
          zipcode: '',
          number: '',
        },
      },
    },
    initialErrors: { user: {}, condominium: {} },
    validationSchema: yup.object({
      user: userSchema,
      condominium: condominiumSchema,
    }),
    onSubmit: async ({ user: { isJanitorAndDweller, ..._user }, condominium: _condominium }) => {
      const user = { ..._user, role: 'janitor' };
      const condominium = { ..._condominium };
      if (condominium.country && condominium.phoneNumber) {
        condominium.phoneNumber = makePhoneNumber({
          country: condominium.country,
          phone: condominium.phoneNumber,
        });
      }
      if (user.country && user.phoneNumber) {
        user.phoneNumber = makePhoneNumber({
          country: user.country,
          phone: user.phoneNumber,
        });
      }
      user.country = undefined;
      condominium.country = undefined;
      await updateCurrentUser(user);
      return registerFn({ variables: { user, condominium } });
    },
  });

  useQuery(condominiumGql, {
    skip: !me || !querystring.get('slug'),
    variables: { slug: querystring.get('slug') },
    onCompleted: ({ condominium }) => {
      if (!condominium) return;
      if (me.condominiums.findIndex(x => x.data === condominium._id) >= 0) {
        history.push('/dashboard');
      }
    },
  });

  useEffect(() => {
    const { currentUser } = firebase.auth();
    if (currentUser) {
      // formikBag.setFieldValue('user.phoneNumber', currentUser.phoneNumber);
      formikBag.setFieldValue(
        'user.phoneNumber',

        (currentUser.phoneNumber &&
          showPhoneNumber(currentUser.phoneNumber.trim())
            .substring(3)
            .trim()) ||
          '',
      );
      formikBag.setFieldValue('user.email', currentUser.email || '');
      formikBag.setFieldValue('user.name', currentUser.displayName || '');
    } else {
      history.push('/');
    }
  }, [history]);

  return (
    <FormikProvider value={formikBag}>
      {formikBag.values.step === 'user' ? <UserRegister /> : <CondominiumRegister />}
    </FormikProvider>
  );
}

Register.propTypes = {
  history: propTypes.object.isRequired,
};
